import * as React from 'react';
import { memo, useMemo, type JSX } from 'react';
import Image from 'next/image';
import { Badge as FlowBiteBadge } from 'flowbite-react';
import { BuildingOfficeIcon, CheckBadgeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import {
  cn,
  JobSimilarPositionsType,
  NOfficeBuildingIcon,
  NoLocationMarkerIcon,
  PrivateModeProtectionIcon,
  SkeletonPhotoBorderedIcon,
  customImageLoader,
  formatDate,
  getCurrencySymbol,
} from '@restworld/utils-common';

import { LinkBase, ParagraphLabelWithIcon } from '@restworld/ui-components';
import { Card } from '../card/card.wrapper';
import { Heading, Text } from '@restworld/ui-ds-base';

const LinkWrapper = memo(
  ({
    children,
    redirectUrl,
    onLinkClick,
  }: {
    children?: React.ReactNode;
    redirectUrl?: string;
    onLinkClick?: () => void;
  }) =>
    redirectUrl || onLinkClick ? (
      <LinkBase
        twStyle={'flex flex-col w-full gap-4 justify-between flex-1'}
        onLinkClick={onLinkClick}
        link={redirectUrl}
        data-cy="redirect-link"
      >
        {children}
      </LinkBase>
    ) : (
      <CardWrapper>{children}</CardWrapper>
    )
);

export interface JobPositionCardProps extends JobSimilarPositionsType {
  redirectEl?: JSX.Element;
  isActive?: boolean;
  isLoading?: boolean;
  ambassadorCode?: string;
  id?: string;
  cardVariant?: 'job-offer' | 'restaurant' | 'job-proposal'; // variant one is for job offers and two is for local offers / restaurant
  onLinkClick?: () => void;
  actionButtonsEl?: JSX.Element;
  twStyle?: string | string[];
  hasAlreadyApplied?: boolean;
  restaurantAddress?: string;
  testId?: string;
  isJobPositionOutsideItaly?: boolean;
}
export function JobPositionCard({
  showRestaurantDetails,
  restaurantName,
  city,
  jobTitle,
  occupation,
  salaryMin,
  salaryMax,
  salaryCurrency,
  jobType,
  publishedAt,
  redirectUrl: baseRedirectUrl,
  restaurantLogo,
  isActive,
  isLoading,
  ambassadorCode,
  id,
  cardVariant = 'job-offer',
  onLinkClick,
  actionButtonsEl,
  twStyle,
  hasAlreadyApplied,
  restaurantAddress,
  testId,
  isJobPositionOutsideItaly,
}: JobPositionCardProps) {
  const { t } = useTranslation();
  const currencySymbol = useMemo(() => getCurrencySymbol(salaryCurrency), [salaryCurrency]);
  const redirectUrl = useMemo(
    () =>
      ambassadorCode
        ? `${baseRedirectUrl}?utm_source=referral&utm_medium=ambassador&utm_campaign=worker_acquisition&ref_code=${ambassadorCode}`
        : baseRedirectUrl,
    [baseRedirectUrl, ambassadorCode]
  );

  const {
    buildingOfficeBlackIconEl,
    nOfficeBuildingIconEl,
    mapPinIconEl,
    noLocationMarkerIconEl,
    privateModeProtectionIconEl,
    mapPinIconPurple,
    privateModeProtectionPurpleIcon,
  } = useMemo(
    () => ({
      buildingOfficeBlackIconEl: <BuildingOfficeIcon className="h-5 w-5 text-gray-900" />,
      nOfficeBuildingIconEl: <NOfficeBuildingIcon />,
      mapPinIconEl: <MapPinIcon className="h-5 text-black" />,
      noLocationMarkerIconEl: <NoLocationMarkerIcon />,
      privateModeProtectionIconEl: <PrivateModeProtectionIcon />,
      mapPinIconPurple: <MapPinIcon className="h-5 w-5 text-primary-800" />,
      privateModeProtectionPurpleIcon: <PrivateModeProtectionIcon stroke="#693EB5" />,
    }),
    []
  );

  return isLoading ? (
    <JobPositionCardLoader {...{ cardVariant }} />
  ) : (
    <Card
      testId={testId}
      variant={cardVariant !== 'restaurant' ? 'primary50' : 'gray100'}
      isActive={isActive}
      twStyle={
        cardVariant !== 'restaurant'
          ? 'transition-all duration-300 ease-in-out border border-transparent shadow-none hover:border-primary-800 hover:bg-[#EFEDFF] focus:border-primary-800 focus:bg-[#EFEDFF]'
          : 'transition-all duration-300 ease-in-out border border-gray-300 shadow-none min-h-[8rem] hover:border-gray-500 focus:border-gray-500'
      }
    >
      <Wrapper cardVariant={cardVariant} twStyle={twStyle}>
        <LinkWrapper redirectUrl={redirectUrl} onLinkClick={onLinkClick}>
          <RestaurantHeaderDetails>
            <RestaurantNameLogo>
              <RestaurantNameLogoTitle>
                <RestaurantCityName>
                  {
                    {
                      'job-offer': showRestaurantDetails ? (
                        <ParagraphLabelWithIcon
                          label={restaurantName ? restaurantName : t('common:name_hidden')}
                          icon={restaurantName ? buildingOfficeBlackIconEl : nOfficeBuildingIconEl}
                          maxChars={30}
                        />
                      ) : undefined,
                      restaurant: (
                        <Heading as="span" size={5} weight="semibold" maxChars={30}>
                          {restaurantName ?? t('common:name_hidden')}
                        </Heading>
                      ),
                      'job-proposal': showRestaurantDetails ? (
                        <ParagraphLabelWithIcon
                          label={restaurantName ? restaurantName : t('common:name_hidden')}
                          icon={restaurantName ? buildingOfficeBlackIconEl : nOfficeBuildingIconEl}
                          maxChars={30}
                        />
                      ) : undefined,
                    }[cardVariant]
                  }
                  {showRestaurantDetails && cardVariant !== 'restaurant' ? (
                    <ParagraphLabelWithIcon
                      label={city || t('common:hidden_location')}
                      icon={city ? (isJobPositionOutsideItaly ? '🌍' : mapPinIconEl) : noLocationMarkerIconEl}
                    />
                  ) : undefined}

                  {!showRestaurantDetails && cardVariant !== 'restaurant' && (
                    <ParagraphLabelWithIcon label={t('common:hidden_location')} icon={privateModeProtectionIconEl} />
                  )}
                </RestaurantCityName>
                {cardVariant !== 'restaurant' && (
                  <Heading as="span" size={5} weight="semibold" maxChars={70}>
                    {jobTitle}
                  </Heading>
                )}
                {restaurantAddress && cardVariant === 'restaurant' && showRestaurantDetails && (
                  <ParagraphLabelWithIcon label={restaurantAddress} icon={mapPinIconPurple} maxChars={36} />
                )}
                {!showRestaurantDetails && cardVariant === 'restaurant' && (
                  <ParagraphLabelWithIcon label={t('common:hidden_location')} icon={privateModeProtectionPurpleIcon} />
                )}
              </RestaurantNameLogoTitle>
              {restaurantLogo && showRestaurantDetails && <ImageLogo logo={restaurantLogo} />}
            </RestaurantNameLogo>
          </RestaurantHeaderDetails>
          {cardVariant !== 'restaurant' ? (
            <OccupationSalaryWrapper>
              {occupation ? <Badge>{occupation}</Badge> : null}
              {salaryMin && salaryMax ? (
                <Badge>
                  {currencySymbol}
                  {salaryMin}-{salaryMax}
                </Badge>
              ) : null}
              {jobType ? <Badge>{jobType}</Badge> : null}
            </OccupationSalaryWrapper>
          ) : null}
          <PublishLocationWrapper>
            <Text size={2} color="default">
              {cardVariant === 'restaurant' ? t('common:registered_by') : t('common:published_by')}{' '}
              {publishedAt ? formatDate(publishedAt) : '-'}
            </Text>
            {hasAlreadyApplied && cardVariant !== 'restaurant' && (
              <HasAlreadyAppliedBadge>
                <HasAppliedContainer>
                  <CheckBadgeIcon className="w-[14px] h-[14px]  text-primary-900" />
                  <Text size={2} className="text-primary-900">
                    {t('common:you_applied')}*
                  </Text>
                </HasAppliedContainer>
              </HasAlreadyAppliedBadge>
            )}
          </PublishLocationWrapper>
        </LinkWrapper>
        {actionButtonsEl ? <Footer>{actionButtonsEl ? actionButtonsEl : null}</Footer> : null}
      </Wrapper>
    </Card>
  );
}

export default JobPositionCard;
const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col flex-1 w-full gap-4 justify-between', className)} {...props} />
);
const Wrapper = ({
  cardVariant,
  twStyle,
  className,
  children,
  ...props
}: Pick<JobPositionCardProps, 'cardVariant' | 'twStyle'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // flex-1 so the content fills the card's height (cards are stretched to equal height inside
      // the carousel); with justify-between this keeps the footer CTA pinned to the card bottom
      // instead of floating with empty space below on shorter cards.
      'flex flex-1 flex-col justify-between gap-4 2xl:min-h-[11rem] xl:min-h-[12.5rem] lg:min-h-[12rem] min-h-[11rem] w-full',
      cardVariant === 'restaurant' && '!min-h-[8rem]',
      cardVariant === 'job-proposal' && '!min-h-[17rem]',
      twStyle,
      className
    )}
    {...props}
  >
    {children}
  </div>
);
const RestaurantHeaderDetails = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-start justify-start gap-2', className)} {...props} />
);
const RestaurantNameLogoTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col flex-1 justify-start items-start w-full gap-2', className)} {...props} />
);
const RestaurantNameLogo = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-row justify-start items-start w-full gap-3', className)} {...props} />
);
const RestaurantCityName = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-1 flex-row justify-start items-start gap-2', className)} {...props} />
);

const OccupationSalaryWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-start gap-2 w-full flex-wrap', className)} {...props} />
);
const Badge = ({ className, ...props }: React.ComponentProps<typeof FlowBiteBadge>) => (
  <FlowBiteBadge className={cn('py-2 px-1 bg-white rounded-lg text-gray-600 font-normal', className)} {...props} />
);
const PublishLocationWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center justify-between [&>div:first-of-type]:grow-[2]', className)} {...props} />
);

const HasAppliedContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-row gap-1 items-center', className)} {...props} />
);

const HasAlreadyAppliedBadge = ({ className, ...props }: React.ComponentProps<typeof FlowBiteBadge>) => (
  <FlowBiteBadge
    className={cn('py-0.5 px-2.5 bg-secondary-100 rounded-[6px] text-gray-600 font-normal h-[22px]', className)}
    {...props}
  />
);

const ImageLogo = ({ logo }: ImageLogoProps) => {
  return (
    <ImageWrapper>
      <Image
        loader={customImageLoader}
        className="object-center"
        width={48}
        height={48}
        src={logo}
        alt="company logo"
      />
    </ImageWrapper>
  );
};

interface ImageLogoProps {
  logo: string;
}
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border-primary-900 w-12 h-12 min-w-[48px] bg-white rounded-[5.28px] flex items-center justify-center py-[17.15px] px-[3.96px]',
      className
    )}
    {...props}
  />
);

const Footer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3', className)} {...props} />
);

const LoaderWrapper = ({ animation, className, ...props }: any) => (
  <div className={cn(animation ? 'animate-pulse' : '', 'w-full', className)} {...props} />
);

export const JobPositionCardLoader = ({
  animation = true,
  cardVariant,
}: { animation?: boolean } & Pick<JobPositionCardProps, 'cardVariant'>) => (
  <LoaderWrapper {...{ animation }}>
    <div role="status" className="flex flex-col shadow-lg  rounded-3.75 w-full bg-gray-100 p-4">
      <div className="flex flex-row items-start justify-start gap-4">
        <div className="flex flex-col flex-1 items-start gap-8 justify-start">
          <div className="h-1.5 bg-gray-300 rounded-[12px] w-1/2 " />
          <div className="flex flex-col w-full items-start justify-start pr-[23px] gap-8">
            <div className="flex flex-col w-full items-start justify-start gap-4">
              <div className="h-1.5 w-full bg-gray-300 rounded-3.75" />
              <div className="h-1.5 w-full bg-gray-300 rounded-3.75" />
            </div>
            <div className="grid grid-cols-3 w-full gap-2">
              <div className="h-[29px] w-full bg-gray-300 rounded-lg" />
              <div className="h-[29px] w-full bg-gray-300 rounded-lg" />
              <div className="h-[29px] w-full bg-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="h-1.5 bg-gray-300 rounded-[12px] w-1/2 " />
          {cardVariant === 'job-proposal' && <div className="h-1.5 bg-gray-300 rounded-[12px] w-1/2 " />}
          {cardVariant === 'job-proposal' && (
            <div className="grid grid-cols-2 w-full gap-2  pr-[23px]">
              <div className="h-[49px] w-full bg-gray-300 rounded-lg" />
              <div className="h-[49px] w-full bg-gray-300 rounded-lg" />
            </div>
          )}
        </div>
        <SkeletonPhotoBorderedIcon className="w-12 h-12 text-gray-300" />
      </div>
    </div>
  </LoaderWrapper>
);
